import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./CSS/BooksContent.module.css";
import { Edit, Trash, Plus, Search } from "lucide-react";
import { useDeleteEbook, useEbooks } from "../../../hooks/admin/useAdminEbooks";
import Loading from "../../../common/HousGlass Loader/Loading";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};

// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Search Component
const SearchBar = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <div className={styles.searchContainer}>
      <input
        type="text"
        placeholder="Search by title..."
        value={searchTerm}
        onChange={handleSearch}
        className={styles.searchInput}
      />
      <Search className={styles.searchIcon} size={18} />
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel, isLoading }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>

        <div className={styles.modalButtons}>
          <button 
            className={styles.modalButton} 
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Confirm'}
          </button>
          <button 
            className={styles.modalButton} 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const EbooksContent = () => {
  const navigate = useNavigate();
  const { ebooks, isLoading, refetchEbooks } = useEbooks();
  const { deleteEbook, isLoading: isDeleting } = useDeleteEbook();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showModal, setShowModal] = useState(false);
  const [ebookToDelete, setEbookToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEbooks, setFilteredEbooks] = useState([]);

  // Update filteredEbooks whenever ebooks or searchTerm changes
  useEffect(() => {
    if (ebooks) {
      const filtered = searchTerm
        ? ebooks.filter((ebook) =>
            ebook.title.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : ebooks;
      
      setFilteredEbooks(filtered);
      // Reset to first page when search results change
      setCurrentPage(1);
    }
  }, [ebooks, searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit-ebook/${id}`);
  };

  // Opens the confirmation modal instead of deleting immediately
  const confirmDelete = (id) => {
    setEbookToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion
  const confirmDeleteAction = async () => {
    if (ebookToDelete !== null) {
      try {
        await deleteEbook(ebookToDelete);
        setEbookToDelete(null);
        setShowModal(false);
        refetchEbooks();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const cancelDelete = () => {
    setEbookToDelete(null);
    setShowModal(false);
  };

  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (!ebooks || ebooks.length === 0) {
    return (
      <div className={styles.empty}>
        <p>No ebooks found</p>
        <button 
          className={styles.addButton}
          onClick={() => navigate('/admin/add-ebook')}
        >
          <Plus size={20} />
          Add Ebook
        </button>
      </div>
    );
  }

  // Pagination logic - adjusted to work with filtered data
  const indexOfLastBook = currentPage * itemsPerPage;
  const indexOfFirstBook = indexOfLastBook - itemsPerPage;
  const currentEbooks = filteredEbooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredEbooks.length / itemsPerPage);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div>
      <div className={styles.header}>
        <h1>Ebook Management</h1>
        <SearchBar onSearch={handleSearch} />
        <div className={styles.buttonGroup}>
          <button 
            className={styles.addButton}
            onClick={() => navigate('/admin/add-multiple-ebook')}
          >
            <Plus size={20} />
            Add Multiple Ebook
          </button>
          <button 
            className={styles.addButton}
            onClick={() => navigate('/admin/add-ebook')}
          >
            <Plus size={20} />
            Add Ebook
          </button>
        </div>
      </div>

      {filteredEbooks.length === 0 ? (
        <div className={styles.noResults}>
          <p>No ebooks found matching your search.</p>
        </div>
      ) : (
        <div className={styles.booksContainer}>
          {currentEbooks.map((ebook) => (
            <div key={ebook.id} className={styles.bookItem}>
              <img
                src={ebook.cover_image_url_for_computer || "/images/placeholders/ebook-cover.png"}
                alt={ebook.title}
                className={styles.bookImage}
              />
              <div className={styles.bookTitle}>
                {ebook.title}
                {ebook.genre_name && <span className={styles.bookGenre}>{ebook.genre_name}</span>}
              </div>
              <div className={styles.iconsOverlay}>
                <EditIconComponent id={ebook.id} onEdit={handleEdit} />
                <DeleteIconComponent id={ebook.id} onDelete={confirmDelete} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls - only show if there are results */}
      {filteredEbooks.length > 0 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={goToPrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className={styles.paginationButton}
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message={`Are you sure you want to delete "${ebooks.find(e => e.id === ebookToDelete)?.title}"?`}
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default EbooksContent;